import { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, Search, Trash2 } from 'lucide-react';
import client, { apiError } from './adminApi';
import { ConfirmDialog, Pagination } from './components/ui';
import { CrmBadge, CrmSyncPanel } from './components/CrmSync';
import { cn } from '@/lib/utils';

const STATUSES = ['new', 'reviewed', 'contacted', 'sent_to_crm', 'closed', 'spam'];

// Lightweight website-enquiry management — deliberately NOT a CRM: no
// pipelines, opportunities or account management.
export default function EnquiriesPage() {
  const [tab, setTab] = useState('enquiries');
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [owner, setOwner] = useState('');
  const [error, setError] = useState('');
  const [toDelete, setToDelete] = useState(null); // { kind: 'enquiry'|'subscription', record }

  const removeRecord = async () => {
    if (!toDelete) return;
    const target = toDelete;
    try {
      const path = target.kind === 'enquiry'
        ? `/admin/enquiries/${target.record.enquiryId}`
        : `/admin/subscriptions/${target.record.subscriptionId}`;
      await client.delete(path, { params: { confirm: true } });
      setToDelete(null);
      if (target.kind === 'enquiry') {
        if (selected?.enquiryId === target.record.enquiryId) setSelected(null);
        load();
      } else {
        setSubs(null);
        loadSubs();
      }
    } catch (err) {
      setToDelete(null);
      setError(apiError(err, 'Could not delete the record.'));
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await client.get('/admin/enquiries', {
        params: { q: q || undefined, status: status || undefined, page, per_page: 20 },
      });
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Enquiries could not be loaded.'));
    }
  }, [q, status, page]);

  const loadSubs = useCallback(async () => {
    try {
      const res = await client.get('/admin/subscriptions');
      setSubs(res.data);
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'subscriptions' && !subs) loadSubs(); }, [tab, subs, loadSubs]);

  const saveMeta = async () => {
    if (!selected) return;
    try {
      await client.patch(`/admin/enquiries/${selected.enquiryId}`, {
        status: selected.status, owner, note: note.trim() || undefined,
      });
      setNote('');
      setSelected(null);
      await load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Enquiries</h1>
          <p className="mt-1 text-sm text-slate-500">Website enquiries and insights subscriptions.</p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/enquiries.csv`}
          data-testid="enquiries-export"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-navy-900 hover:bg-navy-50"
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" /> Export CSV
        </a>
      </div>

      <div className="mt-4 flex gap-2" role="tablist" aria-label="Enquiry views">
        {[['enquiries', 'Website Enquiries'], ['subscriptions', 'Insights Subscriptions']].map(([key, label]) => (
          <button
            key={key} type="button" role="tab" aria-selected={tab === key}
            onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className={cn('rounded-full border-2 px-4 py-2 text-xs font-bold', tab === key ? 'border-navy-900 bg-navy-900 text-white' : 'border-slate-200 bg-white text-navy-800')}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p>}

      {tab === 'enquiries' && (
        <>
          <div className="mt-4 flex flex-wrap gap-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, email, business…" aria-label="Search enquiries" data-testid="enquiries-search" className="h-10 w-72 rounded-md border border-slate-300 bg-white pl-9 pr-3 text-sm" />
            </div>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="Filter by status" data-testid="enquiries-status-filter" className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>

          {!data ? (
            <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>
          ) : data.items.length === 0 ? (
            <p className="mt-6 rounded-xl border-2 border-dashed border-slate-300 bg-white p-10 text-center text-sm font-semibold text-slate-500" data-testid="enquiries-empty">No enquiries found.</p>
          ) : (
            <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                    <th scope="col" className="px-4 py-3">Ref</th>
                    <th scope="col" className="px-4 py-3">From</th>
                    <th scope="col" className="px-4 py-3">Area</th>
                    <th scope="col" className="px-4 py-3">Source</th>
                    <th scope="col" className="px-4 py-3">Status</th>
                    <th scope="col" className="px-4 py-3">Received</th>
                    <th scope="col" className="px-4 py-3"><span className="sr-only">Open</span></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((e) => (
                    <tr key={e.enquiryId} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-navy-900">{e.enquiryId}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-navy-900">{e.name}</p>
                        <p className="text-xs text-slate-500">{e.businessName}</p>
                      </td>
                      <td className="px-4 py-3 capitalize text-slate-600">{e.supportRequired}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{e.source}</td>
                      <td className="px-4 py-3">
                        <span className={cn('rounded-full px-2.5 py-1 text-[11px] font-bold uppercase', e.status === 'new' ? 'bg-blue-100 text-blue-800' : e.status === 'spam' ? 'bg-red-100 text-red-700' : e.status === 'sent_to_crm' ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-600')}>
                          {e.status.replace('_', ' ')}
                        </span>
                        <CrmBadge record={e} />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(e.submittedAt).toLocaleDateString('en-IE')}</td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" onClick={() => { setSelected(e); setOwner(e.internalOwner || ''); }} data-testid={`enquiry-open-${e.enquiryId}`} className="text-sm font-bold text-navy-900 underline-offset-2 hover:underline">Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && <Pagination page={page} perPage={20} total={data.total} onPage={setPage} />}
        </>
      )}

      {tab === 'subscriptions' && (
        !subs ? (
          <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>
        ) : subs.items.length === 0 ? (
          <p className="mt-6 rounded-xl border-2 border-dashed border-slate-300 bg-white p-10 text-center text-sm font-semibold text-slate-500">No subscriptions yet.</p>
        ) : (
          <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                  <th scope="col" className="px-4 py-3">Ref</th>
                  <th scope="col" className="px-4 py-3">Name</th>
                  <th scope="col" className="px-4 py-3">Email</th>
                  <th scope="col" className="px-4 py-3">Status</th>
                  <th scope="col" className="px-4 py-3">Consented</th>
                  <th scope="col" className="px-4 py-3"><span className="sr-only">Delete</span></th>
                </tr>
              </thead>
              <tbody>
                {subs.items.map((s) => (
                  <tr key={s.subscriptionId} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3 font-mono text-xs font-bold text-navy-900">{s.subscriptionId}</td>
                    <td className="px-4 py-3 font-semibold text-navy-900">{s.firstName}{s.businessName ? ` · ${s.businessName}` : ''}</td>
                    <td className="px-4 py-3 text-slate-600">{s.email}</td>
                    <td className="px-4 py-3 text-xs font-bold uppercase text-slate-600">{s.status}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(s.consentTimestamp).toLocaleDateString('en-IE')}</td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => setToDelete({ kind: 'subscription', record: s })} data-testid={`sub-delete-${s.subscriptionId}`} aria-label={`Delete subscription for ${s.email}`} className="text-slate-400 transition-colors hover:text-red-700">
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/60 p-4" role="dialog" aria-modal="true" aria-labelledby="enquiry-title">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6" data-testid="enquiry-detail">
            <h2 id="enquiry-title" className="text-lg font-extrabold text-navy-900">{selected.enquiryId} — {selected.name}</h2>
            <p className="mt-1 text-xs text-slate-500">{selected.source} · {new Date(selected.submittedAt).toLocaleString('en-IE')}</p>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Business</dt><dd className="font-semibold text-navy-900">{selected.businessName}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd className="font-semibold text-navy-900">{selected.email}</dd></div>
              {selected.phone && <div className="flex justify-between"><dt className="text-slate-500">Phone</dt><dd className="font-semibold text-navy-900">{selected.phone}</dd></div>}
              {selected.location && <div className="flex justify-between"><dt className="text-slate-500">Location</dt><dd className="font-semibold text-navy-900">{selected.location}</dd></div>}
              <div className="flex justify-between"><dt className="text-slate-500">Area</dt><dd className="font-semibold capitalize text-navy-900">{selected.supportRequired}</dd></div>
              {selected.preferredContactMethod && <div className="flex justify-between"><dt className="text-slate-500">Prefers</dt><dd className="font-semibold capitalize text-navy-900">{selected.preferredContactMethod}</dd></div>}
              <div className="flex justify-between"><dt className="text-slate-500">CRM sync</dt><dd className="font-semibold text-navy-900">{selected.crmSyncStatus}</dd></div>
            </dl>
            <p className="mt-3 rounded-md bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">{selected.message}</p>
            {selected.utm && <p className="mt-2 text-xs text-slate-400">UTM: {JSON.stringify(selected.utm)}</p>}
            {(selected.internalNotes || []).map((n, i) => (
              <p key={i} className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900"><strong>{n.by}:</strong> {n.text}</p>
            ))}

            <CrmSyncPanel kind="enquiry" record={selected} onUpdate={(item) => { setSelected(item); load(); }} />

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="enq-status" className="text-xs font-bold text-navy-900">Status</label>
                <select id="enq-status" value={selected.status} onChange={(e) => setSelected({ ...selected, status: e.target.value })} data-testid="enquiry-status" className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm">
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="enq-owner" className="text-xs font-bold text-navy-900">Internal owner</label>
                <input id="enq-owner" value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="enquiry-owner" className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" placeholder="e.g. Daniel" />
              </div>
            </div>
            <div className="mt-3">
              <label htmlFor="enq-note" className="text-xs font-bold text-navy-900">Private note</label>
              <input id="enq-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" placeholder="Optional note added on save" />
            </div>
            <div className="mt-5 flex items-center justify-between gap-2">
              <button type="button" onClick={() => setToDelete({ kind: 'enquiry', record: selected })} data-testid="enquiry-delete" className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelected(null)} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-navy-900">Close</button>
                <button type="button" onClick={saveMeta} data-testid="enquiry-save" className="rounded-md bg-navy-900 px-4 py-2 text-xs font-bold text-white hover:bg-navy-700">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!toDelete}
        title={toDelete?.kind === 'subscription' ? 'Delete this subscription?' : 'Delete this enquiry?'}
        message={toDelete?.record?.crmSyncStatus === 'synced'
          ? `This record was synced to Amden Reach (${toDelete.record.crmReach?.opportunityCode || toDelete.record.crmReach?.customerCode || 'synced'}). Deleting removes only the website copy — the Reach record stays and must be removed in Reach separately. This cannot be undone.`
          : 'This permanently deletes the record from the website. This cannot be undone.'}
        destructive
        confirmLabel="Delete Permanently"
        onCancel={() => setToDelete(null)}
        onConfirm={removeRecord}
      />
    </div>
  );
}
